import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ContracteProgramariDetaliiComponent } from './contracte-programari-detalii/contracte-programari-detalii.component';
import { BeneficiariService } from '../beneficiari/beneficiari.service';
import { OperatoriService } from 'src/app/platform/nomenclatoare/activities-prog-setari/operatori/operatori.service';
import { SpecializariService } from 'src/app/platform/nomenclatoare/activities-prog-setari/specializari/specializari.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { ContracteProgramariService } from './contracte-programari.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-contracte-programari',
    templateUrl: './contracte-programari.component.html',
    styleUrls: ['./contracte-programari.component.scss']
})
export class ContracteProgramari implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;

    @ViewChild(PopoverDirective) popover!: PopoverDirective;

    helper = helper;
    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    checkboxSelections = false;

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(2026, 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);

        this.selectedEndDate = new Date(this.today);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);

        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = yearsBack === 0 ? this.today : new Date(year, 11, 31);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    // Date popover filtering

    dateFilteringOptions: { id: number; value: string }[] = [
        { id: 1, value: 'Adăugare' },
        { id: 2, value: 'Actualizare' },
        { id: 3, value: 'Validare' },
        { id: 4, value: 'Invalidare/Anulare' },
        { id: 5, value: 'Finalizare' },
    ];

    dateFilterOption: number = 1;

    defaultParameter: { name: string; id: number } = {
        name: '-- Alegeți --',
        id: -1,
    }

    beneficiari!: any[];
    specializari!: Generic[];
    operatori!: Generic[];

    private destroy$ = new Subject<void>();

    filteredBeneficiari!: any[];
    beneficiariControl = new FormControl();

    filteredSpecializari!: any[];
    specializariControl = new FormControl();

    filteredOperatori!: any[];
    operatoriControl = new FormControl();

    filteredUsers!: any[];
    usersControl = new FormControl();

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;
    dtParams: any = {};

    toggleCard() {
        this.showCard = !this.showCard;
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    searchParamsForm!: FormGroup;

    //Form Initial Values
    initialFormValues = {
        contract_id: '',
        nr_contract: '',
        data_contract: '',
        entity_cnp: '',
        entity_id: -1,
        specializare_id: -1,
        specialist_id: -1,
        user_id: -1,
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            contract_id: new FormControl(''),
            nr_contract: new FormControl(''),
            data_contract: new FormControl(''),
            entity_cnp: new FormControl(''),
            entity_id: new FormControl(-1),
            specializare_id: new FormControl(-1),
            specialist_id: new FormControl(-1),
            user_id: new FormControl(-1),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.filteredBeneficiari = this.beneficiari;
        this.beneficiariControl.setValue(null);

        this.filteredSpecializari = this.specializari;
        this.specializariControl.setValue(null);

        this.filteredOperatori = this.operatori;
        this.operatoriControl.setValue(null);

        this.filteredUsers = this.operatori;
        this.usersControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.showCard = false;
        this.rerenderDT();
    }

    dtLink =
        environment.interop.basePath +
        environment.interop.dgaspc.activitati.contracteProgramari.listDT;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private beneficiariService: BeneficiariService,
        private operatoriService: OperatoriService,
        private specializariService: SpecializariService,
        private contracteProgramariService: ContracteProgramariService,
        private route: ActivatedRoute,
        private router: Router,
        public permissionsService: PermissionsService,
    ) {
        this.tomorrow.setDate(this.today.getDate() + 1);
    }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.loadBeneficiari();
        this.loadSpecializari();
        this.loadOperatori();
        this.renderFieldsSearch();

        this.checkExternalContractView();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.beneficiariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredBeneficiari = this.beneficiari;
                    return of(this.beneficiari);
                }
                return this.filterBeneficiari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredBeneficiari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.specializariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSpecializari = this.specializari;
                    return of(this.specializari);
                }
                return this.filterSpecializari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSpecializari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredOperatori = this.operatori;
                    return of(this.operatori);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatori = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.operatori;
                    return of(this.operatori);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadBeneficiari() {
        this.isFilterLoading = true;

        this.beneficiariService.getBeneficiari().pipe(
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.beneficiari = results;
                this.filteredBeneficiari = results;
            },
            error: (err) => {
                console.error('Error in get beneficiari:', err);
            }
        });
    }

    filterBeneficiari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.beneficiari.filter(beneficiar =>
            this.normalizeString(beneficiar.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onBeneficiarSelectClosed() {
        const selectedValue = this.beneficiariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredBeneficiari.some(beneficiar =>
                beneficiar.id === this.searchParamsForm.get('entity_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.beneficiariControl.setValue(null);
                this.filteredBeneficiari = this.beneficiari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadSpecializari() {
        this.isFilterLoading = true;

        this.specializariService.listActive().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.specializari = results;
                this.filteredSpecializari = results;
            },
            error: (err) => {
                console.error('Error in get specializari active:', err);
            }
        });
    }

    filterSpecializari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.specializari.filter(specializare =>
            this.normalizeString(specializare.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSpecializareSelectClosed() {
        const selectedValue = this.specializariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSpecializari.some(specializare =>
                specializare.id === this.searchParamsForm.get('specializare_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.specializariControl.setValue(null);
                this.filteredSpecializari = this.specializari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadOperatori() {
        this.isFilterLoading = true;

        this.operatoriService.list().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.operatori = results;
                this.filteredOperatori = results;
                this.filteredUsers = results;
            },
            error: (err) => {
                console.error('Error in get operatori active:', err);
            }
        });
    }

    filterOperatori(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatori.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onOperatoriSelectClosed() {
        const selectedValue = this.operatoriControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredOperatori.some(operator =>
                operator.id_user === this.searchParamsForm.get('specialist_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.operatoriControl.setValue(null);
                this.filteredOperatori = this.operatori;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(operator =>
                operator.id_user === this.searchParamsForm.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.operatori;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    formatDateDt(dateStr: string) {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
    }

    formatDateWithHour(dateStr: string): string {
        const date = new Date(dateStr);

        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');

        return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
    }

    renderDT() {
        this.isLoading = true;

        this.dtOptions = {
            responsive: true,
            serverSide: true,
            searching: false,
            pageLength: 25,
            order: [[0, 'desc']],
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                Object.assign(dataTablesParameters, {
                    ...this.searchParamsForm.value,
                    data_contract: this.searchParamsForm.get('data_contract')?.value
                        ? this.formatDate(this.searchParamsForm.get('data_contract')?.value)
                        : ''
                });

                this.dtParams = { ...dataTablesParameters };

                // dataTablesParameters.dateFilter = +this.dateFilterOption;
                // dataTablesParameters.date_from = this.formatDate(this.selectedStartDate);
                // dataTablesParameters.date_to = this.formatDate(this.selectedEndDate);
                // dataTablesParameters.notificationParameter = this.notificationParameter.id;

                this.http
                    .post<any>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Contract',
                    data: 'nr_contract',
                    render: (data: any, type: any, row: any) => {
                        return `
                            <div>
                                ${data ? data : 'N/A'}/${row.data_contract ? this.formatDateDt(row.data_contract) : 'N/A'}<br>
                                ${row.date_from_contract ? this.formatDateDt(row.date_from_contract) : 'N/A'} - ${row.date_end_contract ? this.formatDateDt(row.date_end_contract) : 'N/A'}
                            </div>
                        `
                    }
                },
                {
                    title: 'Beneficiar',
                    data: 'entity',
                    render: (data: any) => {
                        return data.full_name;
                    }
                },
                {
                    title: 'Programări contractuale',
                    data: 'subcontracts',
                    render: (data: any) => {
                        return data.length;
                    }
                },
                {
                    title: 'Specializări aplicate',
                    data: 'subcontracts',
                    render: (data: any[]) => {
                        if (!data || data.length === 0) return 'N/A';
                        const specializari = [...new Set(data.map(s => s.denumire_specializare))];
                        return specializari
                            .map(s => `<span class="badge bg-info me-1 mb-1">${s}</span>`)
                            .join('');
                    }
                },
                {
                    title: 'Creat de',
                    data: 'created_by_user_name',
                },
                {
                    title: 'Creat la',
                    data: 'created_at',
                    render: (data: any) => {
                        return this.formatDateWithHour(data);
                    }
                },
                {
                    title: 'Actualizat la',
                    data: 'updated_at',
                    render: (data: any) => {
                        return this.formatDateWithHour(data);
                    }
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).off('click');

                $('td:not(:first-child)', row).on('click', () => {
                    this.onRowClick(data);
                });

                return row;
            },
        }
    }

    onRowClick(rowData: Request) {
        this.closePopover();
        this.openInfoDialog(rowData);
    }

    rerenderDT(paginate = false): void {
        this.isLoading = true;
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            this.isLoading = false;
            dtInstance.draw(paginate);
        });
    }

    openInfoDialog(data: any) {
        const dialogRef = this.dialog.open(ContracteProgramariDetaliiComponent, {
            minHeight: '100vh',
            maxHeight: '100vh',
            minWidth: '100vw',
            maxWidth: '100vw',
            disableClose: true,
            data: data.id
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            this.rerenderDT();
        });
    }

    onDateFilterChange(): void {
        this.rerenderDT();
    }

    exportCSV(): void {
        this.contracteProgramariService.exportCSV(this.dtParams).subscribe({
            next: (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `contracte-programari-${this.formatDate(new Date())}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: (err) => console.error('Export CSV error:', err)
        });
    }

    checkExternalContractView(): void {
        const contractId = Number(this.route.snapshot.queryParamMap.get('contractId'));
        if (!contractId) return;

        // Curățăm query param-ul din URL fără reload
        this.router.navigate([], {
            queryParams: { contractId: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });

        setTimeout(() => {
            this.openInfoDialog({ id: contractId });
        }, 300);
    }
}
